import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { Buffer } from 'buffer'

import './assets/styles/bootstrap.custom.css'
import './assets/styles/index.css'
import './i18n'
import App from './App'
import reportWebVitals from './reportWebVitals'
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import axios from "axios";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import PrivateRoute from './components/PrivateRoute'
import AdminRoute from './components/AdminRoute'
import StaffRoute from './components/StaffRoute'
import StoreRoute from './components/StoreRoute'
// Phase 3 — ด่านที่ตัดสินจาก perm key ดู config/routePermissions.js
// ตอนนี้ซ้อนอยู่ "ข้างใน" ด่านเดิม ยังไม่ได้แทนที่ เพราะโหมดเริ่มต้นเป็นแค่
// เตือน ถ้าเอาด่านเดิมออกตอนนี้ หน้าแอดมินจะเปิดให้ทุกคนที่ล็อกอินทันที
// เมื่อ REACT_APP_RBAC_ENFORCE=true แล้วอยู่ตัว ค่อยลบด่านเดิมทิ้ง
import RequirePermission from './components/RequirePermission'
import store from './store'
import { Provider } from 'react-redux'

// ============ Lazy-loaded Screens (Code-Splitting) ============
// Each screen becomes a separate JS chunk — loaded only when navigated to.
const HomeScreen = React.lazy(() => import('./screens/HomeScreen'))
const LoginScreen = React.lazy(() => import('./screens/LoginScreen'))
const ProfileScreen = React.lazy(() => import('./screens/ProfileScreen'))
const ChangePasswordScreen = React.lazy(() => import('./screens/ChangePasswordScreen'))
const NotFoundScreen = React.lazy(() => import('./screens/NotFoundScreen'))

const NoContainerLayout = React.lazy(() => import('./screens/Layouts/NocontainerLayout'))
const ContainerLayout = React.lazy(() => import('./screens/Layouts/ContainerLayout'))
const PcbAssemblyFormScreen = React.lazy(() => import('./screens/PcbAssemblyFormScreen'))
const PcbAssemblySubmissionsScreen = React.lazy(() => import('./screens/PcbAssemblySubmissionsScreen'))
const InvoiceSubmissionsScreen = React.lazy(() => import('./screens/InvoiceSubmissionsScreen'))

const QuotationCreateFromPcbScreen = React.lazy(() => import('./screens/QuotationCreateFromPcbScreen'))
const InvoiceCreatorScreen = React.lazy(() => import('./screens/InvoiceCreatorScreen'))
const CustomPCBSetScreen = React.lazy(() => import('./screens/CustomPCB/CustomPCBSetScreen'))
const CopyPCBSetScreen = React.lazy(() => import('./screens/CopyPCB/CopyPCBSetScreen'))
const GerberViewerScreen = React.lazy(() => import('./screens/GerberViewerScreen'))
const OrderassemblySetScreen = React.lazy(() => import('./screens/OrderAssembly/OrderassemblySetScreen'))

// --- Blog ---
const BlogAdminScreen = React.lazy(() => import('./screens/BlogAdminScreen'))
const BlogDetailScreen = React.lazy(() => import('./screens/BlogDetailScreen'))

// --- Admin ---
// คนและสิทธิ์ (22 ก.ย. 69) — รายชื่อ/การ์ดแผนก → หน้าของคน / หน้าของแผนก · ตารางเดิมย้ายไป /admin/rbac/legacy
// ผังองค์กร / บอร์ดบริหาร (29 ก.ย. 69 · migration 093)
// องค์กรและบุคลากร (29 ก.ย. 69) — รวมคนและสิทธิ์ / ตารางเดิม / ผังองค์กร / ทะเบียนพนักงาน ไว้ที่เดียว
const OrgHubScreen = React.lazy(() => import('./screens/admin/org/OrgHubScreen'))
const OrgUnitScreen = React.lazy(() => import('./screens/admin/org/OrgUnitScreen'))
const PersonScreen = React.lazy(() => import('./screens/admin/org/PersonScreen'))
const BankAccountsScreen = React.lazy(() => import('./screens/admin/BankAccountsScreen'))
// ระบบและความปลอดภัย (22 ต.ค. 69) — รวมหน้าความปลอดภัย + สถานะเครื่อง NAS + ใครออนไลน์
const SystemSecurityScreen = React.lazy(() => import('./screens/admin/SystemSecurityScreen'))
const SignatureManagementScreen = React.lazy(() => import('./screens/SignatureManagementScreen'))
const TaxInvoiceManagementScreen = React.lazy(() => import('./screens/TaxInvoiceManagementScreen'))
const DocumentHubScreen = React.lazy(() => import('./screens/DocumentHubScreen'))
const PermissionMatrixScreen = React.lazy(() => import('./screens/PermissionMatrixScreen'))
const AdminUserListScreen = React.lazy(() => import('./screens/AdminUserListScreen'))
const AdminUserEditScreen = React.lazy(() => import('./screens/AdminUserEditScreen'))
const CustomerHubScreen = React.lazy(() => import('./screens/CustomerHubScreen'))
const ProductionRunArchiveScreen = React.lazy(() => import('./screens/Production/ProductionRunArchiveScreen'))
const ProductionCompareScreen = React.lazy(() => import('./screens/Production/ProductionCompareScreen'))
const ProductionBomHubScreen = React.lazy(() => import('./screens/Production/ProductionBomHubScreen'))
const ProductionBOMStudio = React.lazy(() => import('./screens/Production/ProductionBOMStudio'))
const BomPrintScreen = React.lazy(() => import('./screens/Production/BomPrintScreen'))
const SalesWithdrawalScreen = React.lazy(() => import('./screens/Sales/SalesWithdrawalScreen'))
const SupplierDirectoryScreen = React.lazy(() => import('./screens/Store/SupplierDirectoryScreen'))
const SupplierOverviewScreen = React.lazy(() => import('./screens/Store/SupplierOverviewScreen'))
const ProjectHubScreen = React.lazy(() => import('./screens/Production/ProjectHubScreen'))
const ProjectOverviewScreen = React.lazy(() => import('./screens/Production/ProjectOverviewScreen'))
const RunTestMiniScreen = React.lazy(() => import('./screens/Production/RunTestMiniScreen'))
const NextRunScreen = React.lazy(() => import('./screens/Production/NextRunScreen'))
const ProjectWorkScreen = React.lazy(() => import('./screens/Production/ProjectWorkScreen'))
const ProjectTrackingScreen = React.lazy(() => import('./screens/Production/ProjectTrackingScreen'))
const ProjectLegacyLinkScreen = React.lazy(() => import('./screens/Production/ProjectLegacyLinkScreen'))
const ProductionRunScreen = React.lazy(() => import('./screens/Production/ProductionRunScreen'))
const RunReportScreen = React.lazy(() => import('./screens/Production/RunReportScreen'))
const RunTestScreen = React.lazy(() => import('./screens/Production/RunTestScreen'))
const RunToBomScreen = React.lazy(() => import('./screens/Production/RunToBomScreen'))
const QcProgressReportScreen = React.lazy(() => import('./screens/Production/QcProgressReportScreen'))
const QcHubScreen = React.lazy(() => import('./screens/Production/QcHubScreen'))
const PrototypeReviewScreen = React.lazy(() => import('./screens/Production/PrototypeReviewScreen'))
const CustomerDetailScreen = React.lazy(() => import('./screens/CustomerDetailScreen'))
const CompanyOverviewScreen = React.lazy(() => import('./screens/CompanyOverviewScreen'))
const QuotationWizardScreen = React.lazy(() => import('./screens/wizard/QuotationWizardScreen'))
const InvoiceWizardScreen = React.lazy(() => import('./screens/wizard/InvoiceWizardScreen'))
const TaxInvoiceWizardScreen = React.lazy(() => import('./screens/wizard/TaxInvoiceWizardScreen'))
const CustomerEditScreen = React.lazy(() => import('./screens/CustomerEditScreen'))
const InvoiceListScreen = React.lazy(() => import('./screens/InvoiceListScreen'))
const InvoiceDetailScreen = React.lazy(() => import('./screens/InvoiceDetailScreen'))
const TaxInvoiceListScreen = React.lazy(() => import('./screens/TaxInvoiceListScreen'))
const OrderPCBEditScreen = React.lazy(() => import('./screens/admin/OrderPCBEditScreen'))

// --- Workflow ---
const JobDetailScreen = React.lazy(() => import('./screens/Workflow/JobDetailScreen'))

// --- Quote ---
const QuoteFormScreen = React.lazy(() => import('./screens/Quote/QuoteFormScreen'))
const QuoteListScreen = React.lazy(() => import('./screens/Quote/QuoteListScreen'))
const QuotationSetScreen = React.lazy(() => import('./screens/Quote/QuotationSetScreen'))
const QuotationListScreen = React.lazy(() => import('./screens/QuotationListScreen'))
const QuotationDetailScreen = React.lazy(() => import('./screens/QuotationDetailScreen'))
const QuotationDocumentListScreen = React.lazy(() => import('./screens/QuotationDocumentListScreen'))

// --- Quotation Flow ---
const QFlowJobDetailScreen = React.lazy(() => import('./screens/QuotationFlow/JobDetailScreen'))
const QFlowSaleIntakeFormScreen = React.lazy(() => import('./screens/QuotationFlow/SaleIntakeFormScreen'))
const QFlowSaleIntakeDetailScreen = React.lazy(() => import('./screens/QuotationFlow/SaleIntakeDetailScreen'))
const QFlowBomEditScreen = React.lazy(() => import('./screens/QuotationFlow/BomEditScreen'))
const QFlowCalcEditScreen = React.lazy(() => import('./screens/QuotationFlow/CalcEditScreen'))
const QFlowQuotationDetailScreen = React.lazy(() => import('./screens/QuotationFlow/QuotationDetailScreen'))
const QFlowProductionAssignScreen = React.lazy(() => import('./screens/QuotationFlow/ProductionAssignScreen'))
const QFlowProductionAssignMultiScreen = React.lazy(() => import('./screens/QuotationFlow/ProductionAssignMultiScreen'))
// ระบบงาน (มอบหมายงาน) — แทน /my-tasks และ Workflow เดิม
const TasksScreen = React.lazy(() => import('./screens/Tasks/TasksScreen'))
const TaskNewScreen = React.lazy(() => import('./screens/Tasks/TaskNewScreen'))
const TaskDetailScreen = React.lazy(() => import('./screens/Tasks/TaskDetailScreen'))
const TaskTemplatesScreen = React.lazy(() => import('./screens/Tasks/TaskTemplatesScreen'))

// --- Dashboard ---
const DashboardScreen = React.lazy(() => import('./screens/DashboardScreen'))

// --- Templates & Departments ---

const TrackPage = React.lazy(() => import('./screens/Public/TrackPage'))

// --- Stock ---
const StockProductDashboardScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockProductDashboardScreen'))
const StockProductSetScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockProductSetScreen'))
const StockSyncScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockSyncScreen'))
const StockProductDetailScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockProductDetailScreen'))
const StockProductEditScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockProductEditScreen'))
const StockRequestDashboardScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockRequestDashboardScreen'))
const StockRequestDetailScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockRequestDetailScreen'))
const StockReceiveDashboardScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockReceiveDashboardScreen'))
const StockReceiveDetailScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockReceiveDetailScreen'))
const StockIssueDashboardScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockIssueDashboardScreen'))
const IssueRelinkScreen = React.lazy(() => import('./screens/Stocks/StockProduct/IssueRelinkScreen'))
const StockIssueDetailScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockIssueDetailScreen'))
const StockCartScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockCartScreen'))
// const StockListForIssueScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockListForIssueScreen'))
// const StockUserRequestDashboardScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockUserRequestDashboardScreen'))
const StockUserIssueDashboardScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockUserIssueDashboardScreen'))
const StockCheckBom = React.lazy(() => import('./screens/Stocks/StockProduct/StockCheckBom'))
const StockAdditionCartScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockAdditionCartScreen'))
const StockRequestImportanceScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockRequestImportanceScreen'))
const StockRequestCancelScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockRequestCancelScreen'))
const StockUserRequestImportanceScreen = React.lazy(() => import('./screens/Stocks/StockProduct/StockUserRequestImportanceScreen'))
const StockManageDefaultScreen = React.lazy(() => import('./screens/Stocks/StockComponents/StockManageDefaultScreen'))
const StockCreateCategoryScreen = React.lazy(() => import('./screens/Stocks/StockComponents/StockCreateCategoryScreen'))
const StockEditSupplierScreen = React.lazy(() => import('./screens/Stocks/StockComponents/StockEditSupplierScreen'))
const StockCreateSupplierScreen = React.lazy(() => import('./screens/Stocks/StockComponents/StockCreateSupplierScreen'))
const StockEditSubcategoryScreen = React.lazy(() => import('./screens/Stocks/StockComponents/StockEditSubcategoryScreen'))
const StockCreateSubcategoryScreen = React.lazy(() => import('./screens/Stocks/StockComponents/StockCreateSubcategoryScreen'))
const StockEditManufacturingScreen = React.lazy(() => import('./screens/Stocks/StockComponents/StockEditManufacturingScreen'))
const StockCreateManufacturingScreen = React.lazy(() => import('./screens/Stocks/StockComponents/StockCreateManufacturingScreen'))
const StockEditFootprintScreen = React.lazy(() => import('./screens/Stocks/StockComponents/StockEditFootprintScreen'))
const StockCreateFootprintScreen = React.lazy(() => import('./screens/Stocks/StockComponents/StockCreateFootprintScreen'))
const StockEditCategoryScreen = React.lazy(() => import('./screens/Stocks/StockComponents/StockEditCategoryScreen'))

// --- BOM Tools ---
// /tools/bom-management เลิกใช้แล้ว — ย้ายไป /tools/price-estimate (เหลือหน้าแจ้งย้าย + ดาวน์โหลดงานร่างค้าง)
const BomManagementRetiredPage = React.lazy(() => import('./screens/Tools/BomManagementRetiredPage'))
// /bom-projects เลิกใช้แล้ว (21 ก.ย. 69) — นำเข้าไฟล์ตรงที่คลัง BOM · เหลือหน้าแจ้งย้าย + ส่งชุดที่ค้างเข้า Production BOM
const BomProjectsRetiredPage = React.lazy(() => import('./screens/Stocks/StockProduct/BomProjectsRetiredPage'))
const ProjectGeneratorScreen = React.lazy(() => import('./screens/Tools/ProjectGeneratorScreen'))
const Bom2BuyFinalPage = React.lazy(() => import('./screens/Tools/Bom2BuyFinalPage').then(m => ({ default: m.Bom2BuyFinalPage })))
const Bom2BuyDashboard = React.lazy(() => import('./screens/Tools/Bom2BuyDashboard'))
// ระบบจัดซื้อ (migration 069) — ใบขอซื้อ / ใบสั่งซื้อ
const PurchaseRequestHubScreen = React.lazy(() => import('./screens/Purchasing/PurchaseRequestHubScreen'))
const PurchaseRequestEditorScreen = React.lazy(() => import('./screens/Purchasing/PurchaseRequestEditorScreen'))
const PurchaseOrderHubScreen = React.lazy(() => import('./screens/Purchasing/PurchaseOrderHubScreen'))
const PurchaseApprovalScreen = React.lazy(() => import('./screens/Purchasing/PurchaseApprovalScreen'))
const GoodsCheckHubScreen = React.lazy(() => import('./screens/Purchasing/GoodsCheckHubScreen'))
const GoodsCheckFormScreen = React.lazy(() => import('./screens/Purchasing/GoodsCheckFormScreen'))
const GoodsCheckDetailScreen = React.lazy(() => import('./screens/Purchasing/GoodsCheckDetailScreen'))
const NewPartScreen = React.lazy(() => import('./screens/Purchasing/NewPartScreen'))
const PurchaseOrderDetailScreen = React.lazy(() => import('./screens/Purchasing/PurchaseOrderDetailScreen'))
const PurchaseOrderPrintScreen = React.lazy(() => import('./screens/Purchasing/PurchaseOrderPrintScreen'))
// พิกัดศุลกากร (HS Code) + ใบชี้แจงศุลกากรต่อ PO (30 ก.ย. 69 · migration 104/105)
const CustomsHubScreen = React.lazy(() => import('./screens/Purchasing/CustomsHubScreen'))
const HsCodeScreen = React.lazy(() => import('./screens/Purchasing/HsCodeScreen'))
const HsTypesScreen = React.lazy(() => import('./screens/Purchasing/HsTypesScreen'))
const CustomsDocScreen = React.lazy(() => import('./screens/Purchasing/CustomsDocScreen'))
const CustomsDocPrintScreen = React.lazy(() => import('./screens/Purchasing/CustomsDocPrintScreen'))
const ChatScreen = React.lazy(() => import('./screens/Chat/ChatScreen'))
const BomVerificationPage = React.lazy(() => import('./screens/Tools/BomVerificationPage').then(m => ({ default: m.BomVerificationPage })))
const KicadUpdateQueueScreen = React.lazy(() => import('./screens/Tools/KicadUpdateQueueScreen'))
const KicadRelibScreen = React.lazy(() => import('./screens/Tools/KicadRelibScreen'))
const KiCadLibraryManagerScreen = React.lazy(() => import('./screens/Tools/KiCadLibraryManagerScreen'))
const BomFilterExportPage = React.lazy(() => import('./screens/Tools/BomFilterExportPage').then(m => ({ default: m.BomFilterExportPage })))
const BomComparatorPage = React.lazy(() => import('./screens/Tools/BomComparatorPage').then(m => ({ default: m.BomComparatorPage })))
const PriceEstimateHubScreen = React.lazy(() => import('./screens/Tools/PriceEstimateHubScreen'))
const PriceEstimateEditScreen = React.lazy(() => import('./screens/Tools/PriceEstimateEditScreen'))
const PriceEstimateSettingsScreen = React.lazy(() => import('./screens/Tools/PriceEstimateSettingsScreen'))
const OnlineFlasherScreen = React.lazy(() => import('./screens/Tools/OnlineFlasherScreen'))
// เบิร์นเฟิร์มแวร์: เลือกโปรเจกต์ก่อน แล้วเข้าไปเบิร์นข้างใน (กันหลงเบิร์นผิดงาน) · หน้าเดิมเหลือไว้เป็นหน้าจัดการคลัง
const FlasherProjectsScreen = React.lazy(() => import('./screens/Tools/FlasherProjectsScreen'))
const FlasherWorkspaceScreen = React.lazy(() => import('./screens/Tools/FlasherWorkspaceScreen'))
const FirmwareManageScreen = React.lazy(() => import('./screens/Tools/FirmwareManageScreen'))
const PrinterHubScreen = React.lazy(() => import('./screens/Tools/PrinterHubScreen'))
const PhoneStorageScreen = React.lazy(() => import('./screens/Production/PhoneStorageScreen'))
const PayrollHubScreen = React.lazy(() => import('./screens/admin/PayrollHubScreen'))
const PayrollScreen = React.lazy(() => import('./screens/admin/PayrollCardScreen'))
const MyPayslipScreen = React.lazy(() => import('./screens/MyPayslipScreen'))
const MyToolsScreen = React.lazy(() => import('./screens/MyToolsScreen'))
const ToolInventoryScreen = React.lazy(() => import('./screens/Production/ToolInventoryScreen'))
const AccountingHubScreen = React.lazy(() => import('./screens/Accounting/AccountingHubScreen'))
const SalePaymentScreen = React.lazy(() => import('./screens/Accounting/SalePaymentScreen'))
const CustomerReceiptListScreen = React.lazy(() => import('./screens/Production/CustomerReceiptListScreen'))
const CustomerReceiptDetailScreen = React.lazy(() => import('./screens/Production/CustomerReceiptDetailScreen'))
const CustomerReceiptMatchListScreen = React.lazy(() => import('./screens/Store/CustomerReceiptMatchListScreen'))
const CustomerReceiptMatchScreen = React.lazy(() => import('./screens/Store/CustomerReceiptMatchScreen'))

// ============ Suspense Fallback (Apple-style minimal spinner) ============
const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center min-h-[50vh]">
    <div className="apple-spinner rounded-full h-8 w-8"></div>
  </div>
)

// ============ Suspense Wrapper for lazy routes ============
const L = ({ children }) => <Suspense fallback={<PageLoader />}>{children}</Suspense>

// พาไปหน้าใหม่แต่เก็บ ?category= ไว้ — หน้าแก้ไขสินค้าส่งกลับมาพร้อมหมวดเดิม
const KeepQueryRedirect = ({ to }) => {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

// หน้าคน/แผนก/พนักงานแบบเก่า → หน้าใหม่ใน "องค์กรและบุคลากร" (ลิงก์เก่าในหน้าเงินเดือน ฯลฯ ยังใช้ได้)
const OldPersonRedirect = () => {
  const { id } = useParams()
  return <Navigate to={`/admin/org/people/u${id}`} replace />
}
const OldDeptRedirect = () => {
  const { id } = useParams()
  return <Navigate to={`/admin/org/${id}`} replace />
}
const OldEmployeesRedirect = () => {
  const emp = new URLSearchParams(useLocation().search).get('emp')
  return <Navigate to={emp ? `/admin/org/people/e${emp}` : '/admin/org?tab=people'} replace />
}

window.Buffer = window.Buffer || Buffer

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path='/' element={<App />}>
      {/* กล่องลอย "ปักงานผลิต" (15 ต.ค. 69) — ไม่มีหัวเว็บ/เมนู (App.js) · ยังไม่ล็อกอิน หน้าเองพาไปเข้าสู่ระบบ */}
      <Route path='/mini/run-tests' element={<L><RunTestMiniScreen /></L>} />
      {/* Routes without Container */}
      <Route element={<L><NoContainerLayout /></L>}>
        <Route index={true} path='/' element={<L><HomeScreen /></L>} />
        <Route path='/pcb-assembly-form' element={<L><PcbAssemblyFormScreen /></L>} />
        <Route path='/track/:token' element={<TrackPage />} />
        <Route path='/assemblypcb' element={<L><OrderassemblySetScreen /></L>} />
        <Route path='/custompcb' element={<L><CustomPCBSetScreen /></L>} />
        <Route path='/copypcb' element={<L><CopyPCBSetScreen /></L>} />
        <Route path='/orderpcb' element={<L><GerberViewerScreen /></L>} />
        
        {/* Blog public route */}
        <Route path='/blogpost/:id' element={<L><BlogDetailScreen /></L>} />

        {/* BOM Tool Portal
            เดิมทั้งบล็อกนี้อยู่นอกด่านทุกชั้น — พิมพ์ URL ตรงเข้าได้โดยไม่ต้อง
            ล็อกอินด้วยซ้ำ ทั้งที่ /tools/documents คือรายการใบกำกับภาษี
            RequirePermission ปิดข้อนี้ทันทีแม้ยังไม่เปิดโหมดบังคับ เพราะการ
            บังคับให้ล็อกอินก่อนไม่ใช่กฎใหม่ที่กำลังทดลอง — ทุกหน้าอื่นในระบบ
            บังคับอยู่แล้ว บล็อกนี้ต่างหากที่หลุด */}
        <Route path='' element={<RequirePermission />}>
          {/* Online Firmware Flasher Studio */}
          <Route path='/tools/flasher' element={<L><FlasherProjectsScreen /></L>} />
          <Route path='/tools/flasher/manage' element={<L><FirmwareManageScreen /></L>} />
          <Route path='/tools/flasher/studio' element={<L><OnlineFlasherScreen /></L>} />
          <Route path='/tools/flasher/:projectId' element={<L><FlasherWorkspaceScreen /></L>} />
          <Route path='/production/flasher' element={<L><FlasherProjectsScreen /></L>} />
          <Route path='/production/flasher/manage' element={<L><FirmwareManageScreen /></L>} />
          <Route path='/production/flasher/studio' element={<L><OnlineFlasherScreen /></L>} />
          <Route path='/production/flasher/:projectId' element={<L><FlasherWorkspaceScreen /></L>} />

          {/* ฝาก-คืนโทรศัพท์ฝ่ายผลิต — แทนไฟล์ Excel รายเดือน
              ⚠ ด่านจริงอยู่ที่ API (production.phone_view / phone_manage)
                 RequirePermission ตรงนี้แค่บังคับให้ล็อกอินก่อน */}
          <Route path='/production/phone-storage' element={<L><PhoneStorageScreen /></L>} />

          {/* ทะเบียนพนักงานกลาง — ใช้ร่วมกันทุกโมดูล
              ⚠ ด่านจริงอยู่ที่ API (employee.view / employee.manage) */}
          <Route path='/admin/employees' element={<OldEmployeesRedirect />} />
          {/* องค์กรและบุคลากร — แท็บเห็นตามสิทธิ์ (rbac.manage / employee.view) ด่านจริงอยู่ที่ API */}
          <Route path='/admin/org' element={<L><OrgHubScreen /></L>} />
          <Route path='/admin/org/people/:key' element={<L><PersonScreen /></L>} />
          <Route path='/admin/org/:id' element={<L><OrgUnitScreen /></L>} />

          {/* ระบบเงินเดือน — ด่านจริงอยู่ที่ API (payroll.view_all / payroll.manage) */}
          {/* หน้ารวมเงินเดือน แล้วค่อยเจาะเข้าการ์ดของแต่ละคน
              ⚠ URL บอกได้ว่าดูของใคร กด refresh หรือส่งลิงก์ต่อแล้วยังอยู่ที่เดิม */}
          <Route path='/admin/payroll' element={<L><PayrollHubScreen /></L>} />
          <Route path='/admin/payroll/:employeeId' element={<L><PayrollScreen /></L>} />
          {/* ศูนย์รวมระบบบัญชี — ขายสด/เชื่อ · รายจ่ายสด/เครดิต · ของคงคลัง · สรุปรายเดือน (23 ต.ค. 69) */}
          <Route path='/accounting' element={<L><AccountingHubScreen /></L>} />
          <Route path='/accounting/sales/:invoiceNo/payments' element={<L><SalePaymentScreen /></L>} />
          {/* สลิปของตัวเอง — ทุกคนที่ล็อกอินเข้าได้
              ปลอดภัยเพราะ API หาพนักงานจากบัญชีที่ล็อกอิน ไม่รับ id จาก URL */}
          <Route path='/my-payslip' element={<L><MyPayslipScreen /></L>} />

          {/* เครื่องมือช่าง — แทน Excel ตรวจเช็คเครื่องมือ2569
              /my-tools ทุกคนที่ล็อกอินเห็นของตัวเอง (API หาพนักงานจากบัญชีที่ล็อกอิน ไม่รับ id จาก URL)
              /production/tools คลังจัดการ · ด่านจริงอยู่ที่ API (production.tool_*) */}
          <Route path='/my-tools' element={<L><MyToolsScreen /></L>} />
          <Route path='/production/tools' element={<L><ToolInventoryScreen /></L>} />
          <Route path='/production/runs' element={<L><ProductionRunArchiveScreen /></L>} />
          <Route path='/production/runs/:id' element={<L><ProductionRunScreen /></L>} />
          <Route path='/production/runs/:id/test' element={<L><RunTestScreen /></L>} />
          <Route path='/production/runs/:id/test/report' element={<L><QcProgressReportScreen /></L>} />
          <Route path='/production/qc' element={<L><QcHubScreen /></L>} />
          <Route path='/production/runs/:id/report' element={<L><RunReportScreen /></L>} />
          <Route path='/production/runs/:id/prototype' element={<L><PrototypeReviewScreen /></L>} />
          {/* เบิกก่อน แล้วสรุปเป็น BOM (22 ต.ค. 69) */}
          <Route path='/production/runs/:id/to-bom' element={<L><RunToBomScreen /></L>} />
          <Route path='/projects' element={<L><ProjectHubScreen /></L>} />
          <Route path='/projects/tracking' element={<L><ProjectTrackingScreen /></L>} />
          <Route path='/projects/:code' element={<L><ProjectOverviewScreen /></L>} />
          <Route path='/projects/:code/next-run/:version' element={<L><NextRunScreen /></L>} />
          <Route path='/projects/:code/work' element={<L><ProjectWorkScreen /></L>} />
          <Route path='/projects/legacy-link' element={<L><ProjectLegacyLinkScreen /></L>} />
          <Route path='/production/compare' element={<L><ProductionCompareScreen /></L>} />
          <Route path='/production/boms' element={<L><ProductionBomHubScreen /></L>} />
          <Route path='/production/boms/:id' element={<L><ProductionBOMStudio /></L>} />
          <Route path='/production/boms/:id/print' element={<L><BomPrintScreen /></L>} />
          {/* ใบรับของที่ลูกค้าส่งมาเอง (23 ต.ค. 69 · migration 132) */}
          <Route path='/production/customer-receipts' element={<L><CustomerReceiptListScreen /></L>} />
          <Route path='/production/customer-receipts/:id' element={<L><CustomerReceiptDetailScreen /></L>} />
          {/* ตรวจนับและแมตช์อะไหล่ลูกค้าเข้า BOM สำหรับฝ่ายสโตร์ (23 ต.ค. 69) */}
          <Route path='/store/customer-receipt-matches' element={<L><CustomerReceiptMatchListScreen /></L>} />
          <Route path='/store/customer-receipt-matches/:id' element={<L><CustomerReceiptMatchScreen /></L>} />
          {/* รวมเข้าหน้าคลังสินค้า /components แล้ว (22 ก.ย. 69) — ดู CLAUDE.md "หน้าที่เลิกใช้แล้ว" */}
          <Route path='/store/valuation' element={<Navigate to='/components?tab=value' replace />} />
          <Route path='/sales/withdrawals' element={<L><SalesWithdrawalScreen /></L>} />
          <Route path='/store/suppliers' element={<L><SupplierDirectoryScreen /></L>} />
          <Route path='/store/supplier/:id/overview' element={<L><SupplierOverviewScreen /></L>} />

          {/* เลิกใช้แล้ว — ลิงก์เก่าและบุ๊กมาร์กเจอหน้าแจ้งย้าย ไม่ใช่หน้าว่าง */}
          <Route path='/tools/bom-management' element={<L><BomManagementRetiredPage /></L>} />
          {/* ประเมินราคา - เก็บลงฐานข้อมูล ส่งต่อไปใบเสนอราคาได้ */}
          <Route path='/tools/price-estimate' element={<L><PriceEstimateHubScreen /></L>} />
          {/* ⚠ /settings ต้องมาก่อน /:id ไม่งั้นจะถูกจับเป็น id */}
          <Route path='/tools/price-estimate/settings' element={<L><PriceEstimateSettingsScreen /></L>} />
          <Route path='/tools/price-estimate/:id' element={<L><PriceEstimateEditScreen /></L>} />
          {/* เปิดใบ = โหมดดู · /edit = โหมดแก้ (28 ก.ย. 69) — ตัวเดียวกัน สลับตามเส้นทาง */}
          <Route path='/tools/price-estimate/:id/edit' element={<L><PriceEstimateEditScreen /></L>} />
          <Route path='/tools/bom2buy' element={<L><PurchaseRequestHubScreen /></L>} />
          <Route path='/tools/bom2buy/legacy' element={<L><Bom2BuyFinalPage /></L>} />
          <Route path='/tools/bom2buy/history' element={<L><Bom2BuyDashboard /></L>} />
          <Route path='/tools/bom2buy/:id' element={<L><PurchaseRequestEditorScreen /></L>} />
          <Route path='/purchasing/orders' element={<L><PurchaseOrderHubScreen /></L>} />
          <Route path='/purchasing/approvals' element={<L><PurchaseApprovalScreen /></L>} />
          <Route path='/store/goods-check' element={<L><GoodsCheckHubScreen /></L>} />
          <Route path='/store/goods-check/new/:poId' element={<L><GoodsCheckFormScreen /></L>} />
          <Route path='/store/goods-check/:id' element={<L><GoodsCheckDetailScreen /></L>} />
          <Route path='/store/new-part/:poItemId' element={<L><NewPartScreen /></L>} />
          <Route path='/purchasing/orders/:id' element={<L><PurchaseOrderDetailScreen /></L>} />
          <Route path='/purchasing/orders/:id/print' element={<L><PurchaseOrderPrintScreen /></L>} />
          <Route path='/purchasing/orders/:id/customs' element={<L><CustomsDocScreen /></L>} />
          <Route path='/purchasing/orders/:id/customs/print' element={<L><CustomsDocPrintScreen /></L>} />
          <Route path='/purchasing/customs' element={<L><CustomsHubScreen /></L>} />
          <Route path='/purchasing/hs-codes' element={<L><HsCodeScreen /></L>} />
          <Route path='/purchasing/hs-codes/types' element={<L><HsTypesScreen /></L>} />
          <Route path='/chat' element={<L><ChatScreen /></L>} />
          <Route path='/chat/:roomId' element={<L><ChatScreen /></L>} />
          <Route path='/tools/bom-verify' element={<L><BomVerificationPage /></L>} />
          {/* อัปเดต EPN เลิกใช้ 22 ต.ค. 69 — หน้า BOM ทำครบแล้ว (นำ CSV เข้า · จับคู่คลัง · ส่งรหัสคลังกลับเข้า KiCad) ดู CLAUDE.md */}
          <Route path='/tools/stock-to-csv' element={<Navigate to='/production/boms' replace />} />
          <Route path='/tools/stock-to-csv/history' element={<Navigate to='/production/boms' replace />} />
          <Route path='/tools/kicad-updates' element={<L><KicadUpdateQueueScreen /></L>} />
          <Route path='/tools/kicad-relib/:board' element={<L><KicadRelibScreen /></L>} />
          <Route path='/tools/kicad-coworker' element={<L><KiCadLibraryManagerScreen /></L>} />
          <Route path='/tools/filter-export' element={<L><BomFilterExportPage /></L>} />
          <Route path='/tools/comparator' element={<L><BomComparatorPage /></L>} />
          <Route path='/tools/documents' element={<L><TaxInvoiceListScreen /></L>} />
          <Route path='/tools/tax-invoices' element={<Navigate to="/tools/documents" replace />} />
          <Route path='/tools/calculator' element={<Navigate to='/tools/price-estimate' replace />} />
          <Route path='/tools/printers' element={<L><PrinterHubScreen /></L>} />
        </Route>

        {/* Legacy redirects for old /admin/quotation-flow/* URLs */}
        <Route path='/admin/quotation-flow/templates' element={<Navigate to='/tasks/templates' replace />} />
        <Route path='/admin/quotation-flow/reports' element={<Navigate to='/tasks' replace />} />
        
        {/* Admin routes that need full screen without container */}
        <Route path='' element={<AdminRoute />}>
          <Route path='' element={<RequirePermission />}>
            <Route path='/admin/orderpcbeditlist' element={<L><OrderPCBEditScreen /></L>} />
          </Route>
        </Route>

        {/* 404 — catch-all. อยู่ใน NoContainerLayout เพราะ NotFoundScreen เป็นหน้าเต็มจอ (min-h-screen) เอง */}
        <Route path='*' element={<L><NotFoundScreen /></L>} />
      </Route>

      {/* Routes with Container */}
      <Route element={<L><ContainerLayout /></L>}>
        <Route path='/login' element={<L><LoginScreen /></L>} />

        {/* Registered users */}
        <Route path='' element={<PrivateRoute />}>
         <Route path='' element={<RequirePermission />}>
          <Route path='/dashboard' element={<L><DashboardScreen /></L>} />
          <Route path='/admin/dashboard' element={<Navigate to="/dashboard" replace />} />
          <Route path='/profile' element={<L><ProfileScreen /></L>} />
          {/* ตั้งรหัสใหม่ — บังคับหลังแอดมินตั้งรหัสชั่วคราว (migration 130) */}
          <Route path='/change-password' element={<L><ChangePasswordScreen /></L>} />
          {/* ระบบเก่าเลิกใช้ 22 ก.ย. 69 — ลบหน้าจอแล้ว ลิงก์เดิมพาไประบบงาน (ดู CLAUDE.md "หน้าที่เลิกใช้แล้ว") */}
          <Route path='/admin/workflows' element={<Navigate to='/tasks' replace />} />
          <Route path='/admin/workflows/create' element={<Navigate to='/tasks' replace />} />
          <Route path='/admin/workflows/:id/assignments' element={<Navigate to='/tasks' replace />} />
          <Route path='/admin/workflows/:id' element={<Navigate to='/tasks' replace />} />
          <Route path='/admin/jobs/:id' element={<L><JobDetailScreen /></L>} />
          <Route path='/admin/blogs' element={<L><BlogAdminScreen /></L>} />
          {/* ระบบงาน — /my-tasks เดิมพามาที่นี่ */}
          <Route path='/tasks' element={<L><TasksScreen /></L>} />
          <Route path='/tasks/new' element={<L><TaskNewScreen /></L>} />
          <Route path='/tasks/templates' element={<L><TaskTemplatesScreen /></L>} />
          <Route path='/tasks/:id' element={<L><TaskDetailScreen /></L>} />
          <Route path='/my-tasks' element={<Navigate to='/tasks' replace />} />
         </Route>
        </Route>

        {/* Staff users */}
        <Route path='' element={<StaffRoute />}>
         <Route path='' element={<RequirePermission />}>
          {/* สร้างโฟลเดอร์บน share ของ R&D — หน้าตั้งค่าในนี้เปิดให้เฉพาะแอดมิน (เช็คซ้ำที่ backend ด้วย) */}
          <Route path='/tools/project-generator' element={<L><ProjectGeneratorScreen /></L>} />
          <Route path='/componentcheckboom' element={<L><StockCheckBom /></L>} />
          <Route path='/bom-projects' element={<L><BomProjectsRetiredPage /></L>} />
          <Route path='/bom-projects/:id' element={<L><BomProjectsRetiredPage /></L>} />
          <Route path='/componentcartlist' element={<L><StockCartScreen /></L>} />
          <Route path='/componentuserissuelist' element={<L><StockUserIssueDashboardScreen /></L>} />
          <Route path='/componentuserissuelist/:id' element={<L><StockIssueDetailScreen /></L>} />
          <Route path='/componenteditlist' element={<KeepQueryRedirect to='/components' />} />
          <Route path='/componenteditlist/:id' element={<L><StockProductDetailScreen /></L>} />
          <Route path='/componenteditlist/:id/edit' element={<L><StockProductEditScreen /></L>} />
          <Route path='/componentuserrequestedimportancelist' element={<L><StockUserRequestImportanceScreen /></L>} />
          <Route path='/componentuserrequestedcancellist' element={<L><StockRequestCancelScreen /></L>} />
          <Route path='/components' element={<L><StockProductDashboardScreen /></L>} />

          {/* ย้ายมาจาก StoreRoute — เดิมอยู่หลังด่านที่เช็ค isStore/แผนก STORE
              ทั้งที่คอมเมนต์เดิมเขียนไว้เองว่า sales กับ production ต้องเข้าได้
              ผลคือคนสองแผนกนั้นเห็นเมนู (navCatalog ให้สิทธิ์ qflow.board)
              แต่กดแล้วเจอหน้า Forbidden — เป็นบั๊กที่เกิดอยู่จริง ไม่ใช่หนี้ทางเทคนิค */}
          {/* Quotation flow — admin AND all staff (production, sales) need these */}
          <Route path='/admin/quotation-flow' element={<Navigate to='/tasks' replace />} />
          <Route path='/admin/quotation-flow/board' element={<Navigate to='/tasks' replace />} />
          <Route path='/admin/quotation-flow/intake/new' element={<L><QFlowSaleIntakeFormScreen /></L>} />
          <Route path='/admin/quotation-flow/intake/:jobId' element={<L><QFlowSaleIntakeDetailScreen /></L>} />
          <Route path='/admin/quotation-flow/bom/:jobId' element={<L><QFlowBomEditScreen /></L>} />
          <Route path='/admin/quotation-flow/calc/:jobId' element={<L><QFlowCalcEditScreen /></L>} />
          <Route path='/admin/quotation-flow/quotations' element={<Navigate to='/tasks' replace />} />
          <Route path='/admin/quotation-flow/quotations/:id' element={<L><QFlowQuotationDetailScreen /></L>} />
          <Route path='/admin/quotation-flow/production/:jobId' element={<L><QFlowProductionAssignScreen /></L>} />
          <Route path='/admin/quotation-flow/production-multi/:jobId' element={<L><QFlowProductionAssignMultiScreen /></L>} />
          <Route path='/admin/quotation-flow/job/:id' element={<L><QFlowJobDetailScreen /></L>} />

          {/* Templates and reports — PRODUCTION needs read access too */}
          <Route path='/admin/templates' element={<Navigate to='/tasks/templates' replace />} />
          <Route path='/admin/templates/create' element={<Navigate to='/tasks/templates' replace />} />
          <Route path='/admin/dept-templates' element={<Navigate to='/tasks/templates' replace />} />
          <Route path='/admin/reports' element={<Navigate to='/tasks' replace />} />
         </Route>
        </Route>

        {/* Store users */}
        <Route path='' element={<StoreRoute />}>
         <Route path='' element={<RequirePermission />}>
          <Route path='/componenteditlist/set' element={<L><StockProductSetScreen /></L>} />
          {/* ซิงก์สินค้าจากเว็บเก่า — เขียนข้อมูลสต็อก จึงคุมระดับเดียวกับหน้าสร้างสินค้า */}
          <Route path='/components/sync' element={<L><StockSyncScreen /></L>} />
          <Route path='/componentrequestlist' element={<L><StockRequestDashboardScreen /></L>} />
          <Route path='/componentrequestlist/:id' element={<L><StockRequestDetailScreen /></L>} />
          <Route path='/componentreceivelist' element={<L><StockReceiveDashboardScreen /></L>} />
          <Route path='/componentreceivelist/:id' element={<L><StockReceiveDetailScreen /></L>} />
          <Route path='/componentissuelist' element={<L><StockIssueDashboardScreen /></L>} />
          <Route path='/componentissuelist/relink' element={<L><IssueRelinkScreen /></L>} />
          <Route path='/componentissuelist/:id' element={<L><StockIssueDetailScreen /></L>} />
          <Route path='/componentaddproductlist' element={<Navigate to='/components' replace />} />
          <Route path='/componentaddcartlist' element={<L><StockAdditionCartScreen /></L>} />
          <Route path='/componentdefaultlist' element={<L><StockManageDefaultScreen /></L>} />
          <Route path='/componentcreatecategory' element={<L><StockCreateCategoryScreen /></L>} />
          <Route path='/stock/category/:id/edit' element={<L><StockEditCategoryScreen /></L>} />
          <Route path='/componentcreatefootprint' element={<L><StockCreateFootprintScreen /></L>} />
          <Route path='/stock/footprint/:id/edit' element={<L><StockEditFootprintScreen /></L>} />
          <Route path='/componentcreatemanufacturing' element={<L><StockCreateManufacturingScreen /></L>} />
          <Route path='/stock/manufacture/:id/edit' element={<L><StockEditManufacturingScreen /></L>} />
          <Route path='/componentcreatesubcategory' element={<L><StockCreateSubcategoryScreen /></L>} />
          <Route path='/stock/subcategory/:id/edit' element={<L><StockEditSubcategoryScreen /></L>} />
          <Route path='/componentcreatesupplier' element={<L><StockCreateSupplierScreen /></L>} />
          <Route path='/stock/supplier/:id/edit' element={<L><StockEditSupplierScreen /></L>} />
         </Route>
        </Route>

        {/* Admin users */}
        <Route path='' element={<AdminRoute />}>
         <Route path='' element={<RequirePermission />}>
          {/* หน้าเก่าของคน/สิทธิ์/แผนก — ลบแล้ว (29 ก.ย. 69) พาไปหน้าใหม่ */}
          <Route path='/admin/rbac' element={<Navigate to='/admin/org?tab=people' replace />} />
          <Route path='/admin/rbac/legacy' element={<Navigate to='/admin/org' replace />} />
          <Route path='/admin/rbac/people/:id' element={<OldPersonRedirect />} />
          <Route path='/admin/rbac/departments/:id' element={<OldDeptRedirect />} />
          <Route path='/admin/bank-accounts' element={<L><BankAccountsScreen /></L>} />
          <Route path='/admin/security' element={<L><SystemSecurityScreen /></L>} />
          {/* หน้าสถานะเครื่องเดิม → แท็บในหน้า ระบบและความปลอดภัย */}
          <Route path='/admin/system' element={<Navigate to='/admin/security?tab=system' replace />} />
          <Route path='/admin/signatures' element={<L><SignatureManagementScreen /></L>} />
          <Route path='/admin/permissions' element={<Navigate to='/admin/rbac' replace />} />
          <Route path='/admin/departments' element={<Navigate to='/admin/rbac' replace />} />
          <Route path='/admin/userlist' element={<Navigate to='/admin/rbac' replace />} />
          
          <Route path='/admin/quotes' element={<L><QuoteListScreen /></L>} />
          <Route path='/admin/quotations' element={<L><QuotationListScreen /></L>} />
          <Route path='/admin/quotation-documents' element={<L><QuotationDocumentListScreen /></L>} />
          <Route path='/admin/quotes/new' element={<L><QuoteFormScreen /></L>} />
          <Route path='/admin/quotes/:id/edit' element={<L><QuoteFormScreen /></L>} />
          
          {/* New Aligned Quotes & Invoices Routes */}
          <Route path='/admin/quotes-new/:id' element={<L><QuotationDetailScreen /></L>} />
          <Route path='/admin/quotes-new/:id/edit' element={<L><QuotationWizardScreen /></L>} />
          <Route path='/admin/invoices-new/:id/edit' element={<Navigate to="/admin/invoices" replace />} />
          <Route path='/admin/user/:id/edit' element={<L><AdminUserEditScreen /></L>} />
          <Route path='/admin/customers' element={<L><CustomerHubScreen /></L>} />
          <Route path='/admin/customer/:id/overview' element={<L><CompanyOverviewScreen /></L>} />
          <Route path='/admin/customer/:id/quote/new' element={<L><QuotationWizardScreen /></L>} />
          <Route path='/admin/customer/:id/invoice/new' element={<L><InvoiceWizardScreen /></L>} />
          <Route path='/admin/customer/:id/taxinvoice/new' element={<L><TaxInvoiceWizardScreen /></L>} />
          <Route path='/admin/customer/:id' element={<Navigate to="overview" replace />} />
          <Route path='/admin/customer/:id/edit' element={<L><CustomerEditScreen /></L>} />
          <Route path='/admin/invoices' element={<L><InvoiceListScreen /></L>} />
          <Route path='/admin/document-hub' element={<L><DocumentHubScreen /></L>} />
          <Route path='/admin/invoice/:id' element={<L><InvoiceDetailScreen /></L>} />
          <Route path='/admin/invoice/:id/edit' element={<L><InvoiceWizardScreen /></L>} />
          <Route path='/admin/taxinvoices' element={<L><TaxInvoiceManagementScreen /></L>} />
          <Route path='/admin/taxinvoice/:id' element={<L><InvoiceDetailScreen /></L>} />
          {/* แก้ไขใบกำกับภาษี — :id คือเลขที่เอกสาร ไม่ใช่ id ลูกค้า
              (ตาราง tbl_invoices ไม่มีคอลัมน์ customer_id ให้อ้างอิง) */}
          <Route path='/admin/taxinvoice/:id/edit' element={<L><TaxInvoiceWizardScreen /></L>} />
          <Route path='/admin/componentrequestimportancelist' element={<L><StockRequestImportanceScreen /></L>} />
          <Route path='/admin/componentrequestedcancellist' element={<L><StockRequestCancelScreen /></L>} />
          <Route path='/admin/invoice-submissions' element={<L><InvoiceSubmissionsScreen /></L>} />
          <Route path='/admin/pcb-submissions' element={<L><PcbAssemblySubmissionsScreen /></L>} />
          <Route path='/admin/quotations/create-from-pcb/:id' element={<L><QuotationCreateFromPcbScreen /></L>} />
          <Route path='/admin/invoices/create-from-pcb/:id' element={<L><InvoiceCreatorScreen /></L>} />

          <Route path='/admin/pm' element={<Navigate to='/tasks' replace />} />
         </Route>
        </Route>
      </Route>
    </Route>
  ),
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipRouteErrorRevalidation: true,
    },
  }
)



// ============ NProgress Global Configuration ============
// แถบโหลดสีฟ้าด้านบน — โชว์เฉพาะตอนที่คนรอจริง (22 ก.ย. 69)
//   เดิมเริ่มแถบทุกคำขอ แชทลอยดึงข้อความเองทุก 10–30 วิ + กระดิ่งแจ้งเตือน → แถบวิ่งตลอด
//   ผู้ใช้เห็นเหมือนเว็บรีเฟรชเอง ไม่ smooth
//   ตอนนี้: ข้ามคำขอเบื้องหลัง · คำขอเร็วกว่า 400 ms ไม่โชว์ · หลายคำขอพร้อมกันนับรวม จบครบค่อยปิดแถบ
NProgress.configure({ showSpinner: false, speed: 400, minimum: 0.1 });

const BACKGROUND_REQUESTS = [/\/api\/chat\//, /\/api\/notifications/, /\/socket\.io\//, /\/api\/tasks\/meta/];
let inflight = 0;
let showTimer = null;
const progressBegin = (url) => {
  if (BACKGROUND_REQUESTS.some((re) => re.test(String(url || "")))) return false;
  inflight += 1;
  if (inflight === 1) showTimer = setTimeout(() => NProgress.start(), 400);
  return true;
};
const progressEnd = (counted) => {
  if (!counted) return;
  inflight = Math.max(0, inflight - 1);
  if (inflight > 0) return;
  clearTimeout(showTimer);
  showTimer = null;
  if (NProgress.isStarted()) NProgress.done();
};

// Intercept Axios
axios.interceptors.request.use((config) => {
  config.__progress = progressBegin(config.url);
  return config;
});
axios.interceptors.response.use(
  (response) => {
    progressEnd(response.config?.__progress);
    return response;
  },
  (error) => {
    progressEnd(error.config?.__progress);
    return Promise.reject(error);
  }
);

// Intercept Native Fetch (RTK Query)
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const target = args[0];
  const counted = progressBegin(typeof target === "string" ? target : target?.url);
  try {
    return await originalFetch(...args);
  } finally {
    progressEnd(counted);
  }
};
// =========================================================

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store} deferLoading={true}>
        <RouterProvider
          router={router}
          future={{
            v7_startTransition: true,
          }}
        />
      </Provider>
    </HelmetProvider>
  </React.StrictMode>
)

reportWebVitals()
